import { Activity } from "./Activity";
import { Reactions } from "./Reactions";
import { Video } from "./Video";
import type { LivePayload } from "./types";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useT, useText } from "../settings/context";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ L'ÉCRAN DU DIRECT, SANS SON CADRE ═══
 *
 * Le contenu de la page « En direct », extrait de sa `<section class="page">` pour
 * pouvoir être servi à deux endroits : la page du participant, et la régie, qui
 * pilote en voyant ce que voit la salle.
 *
 * ⚠️ UN SEUL COMPOSANT, ET C'EST TOUT L'INTÉRÊT. Une copie « pour l'aperçu de la
 * régie » aurait divergé au premier changement, et la divergence se serait vue le
 * jour d'une plénière : l'organisateur pilote ce qu'il croit voir.
 *
 * ⚠️ CE QUE LA RÉGIE VOIT EN PLUS, ET POURQUOI ON L'ACCEPTE. Le serveur lui envoie
 * la charge complète : sur le module « questions », elle voit donc aussi celles qui
 * attendent la modération, que le participant ne reçoit pas. Ce n'est pas une
 * divergence d'affichage mais de DONNÉES, et la retirer reviendrait à demander au
 * serveur une seconde charge « en tant que participant » — c'est-à-dire à accepter
 * un rôle dicté par le client. Le reste est identique au pixel.
 *
 * Displayed strings are French: this is a French product.
 */
export function LiveView({
  live,
  sequenceId,
  isPending,
  isError,
  preview = false,
}: {
  live: LivePayload | undefined;
  sequenceId: number | null;
  isPending: boolean;
  isError: boolean;
  /** Servi dans l'aperçu de la régie : voir `Video`, qui n'y monte pas le flux. */
  preview?: boolean;
}) {
  const title = useText(
    "FO_MEETING_V2_LIVE_TITLE",
    fallbackOf("FO_MEETING_V2_LIVE_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_LIVE_SUBTITLE",
    fallbackOf("FO_MEETING_V2_LIVE_SUBTITLE"),
  );
  const beforeText = useText(
    "FO_MEETING_V2_LIVE_BEFORE",
    fallbackOf("FO_MEETING_V2_LIVE_BEFORE"),
  );
  const afterText = useText(
    "FO_MEETING_V2_LIVE_AFTER",
    fallbackOf("FO_MEETING_V2_LIVE_AFTER"),
  );
  const html = useText("FO_MEETING_V2_LIVE_HTML");
  const reactionsShown = useFlagOr("FO_MEETING_V2_LIVE_REACTIONS");
  const t = useT();

  return (
    <>
      <div className="ttl" data-cfg="liveTitre">
        <InPlace settingKey="FO_MEETING_V2_LIVE_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_LIVE_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="liveTitre" />
      )}

      {isPending ? (
        <p className="empty">{t("LIVE_LOADING", "Chargement du direct.")}</p>
      ) : null}

      {/* ⚠️ DIRE QUE ÇA NE MARCHE PAS, plutôt qu'un écran vide qui ressemble à un
          direct qui n'a pas commencé. Les deux se distinguent mal, et c'est
          précisément le moment où quelqu'un attend quelque chose. */}
      {isError ? (
        <p className="empty">
          {t(
            "LIVE_UNREACHABLE",
            "Le direct est momentanément injoignable. La page réessaie toute seule.",
          )}
        </p>
      ) : null}

      {/* ⚠️ POSÉ TEL QUEL, comme les autres blocs de prose du produit : ces deux
          messages viennent de colonnes HTML de la V1, et les rendre en texte
          affichait les balises au participant. Même confiance qu'ailleurs, le
          contenu vient du back-office. */}
      {live?.state.broadcast === "before" ? (
        <Prose
          html={beforeText}
          className="empty se-prose"
          />
      ) : null}

      {live?.state.broadcast === "ended" ? (
        <Prose
          html={afterText}
          className="empty se-prose"
          />
      ) : null}

      {live?.state.broadcast === "live" ? (
        <div className="live">
          <Video viewers={live.viewers ?? null} preview={preview} />
          <div className="live__m" data-cfg="liveModule">
            <Activity live={live} />
            {reactionsShown ? <Reactions sequenceId={sequenceId} /> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
